import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { CheckIcon, CircleNotchIcon, XIcon } from '@phosphor-icons/react';
import { ResponsiveDrawer } from '@/components/ui/responsive-drawer';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { cn } from '@/lib/utils';
import {
  recurringPaymentsApi,
  type RecurringPayment,
  type RecurringPaymentCadence,
  type RecurringPaymentKind,
} from '@/lib/api';
import { cadenceLabel as baseCadenceLabel, cadenceSuffix, monthlyEquivalent } from '@/lib/recurring/cadence';
import type { Transaction } from '@/types';
import type { ConnectorBranding } from './ConnectorDot';
import { useRecurringTaxonomyColors } from './taxonomy';

export type ReviewDecision = 'confirmed' | 'dismissed';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: RecurringPayment[];
  connectorFor: (accountId: string | null) => ConnectorBranding | null;
  onResolve: (
    id: string,
    decision: ReviewDecision,
    kind?: RecurringPaymentKind,
    cadence?: RecurringPaymentCadence,
  ) => void;
}

const EDITABLE_KINDS: RecurringPaymentKind[] = ['Subscription', 'PixRecurring', 'Utility'];

export function defaultReviewTipo(kind: RecurringPaymentKind): 'assinatura' | 'conta_fixa' {
  return kind === 'Subscription' ? 'assinatura' : 'conta_fixa';
}

function cadenceLabel(item: RecurringPayment): string {
  const base = baseCadenceLabel(item.cadence);
  return item.dayOfMonth ? `${base} · todo dia ${item.dayOfMonth}` : base;
}

function displayName(item: RecurringPayment): string {
  return item.merchantName ?? item.categoryTranslated ?? item.category ?? 'Recorrente';
}

/** Step-through review of detected candidates: one card at a time, with evidence. */
export function ReviewSheet({ open, onOpenChange, candidates, connectorFor, onResolve }: Props) {
  const [snapshot, setSnapshot] = useState<RecurringPayment[]>([]);
  const [idx, setIdx] = useState(0);
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>({});

  // Snapshot the queue when the sheet opens so background list mutations don't reindex it.
  useEffect(() => {
    if (open) {
      setSnapshot(candidates);
      setIdx(0);
      setDecisions({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const total = snapshot.length;
  const done = idx >= total;
  const current = snapshot[idx];

  const decide = (decision: ReviewDecision, kind?: RecurringPaymentKind, cadence?: RecurringPaymentCadence) => {
    if (!current) return;
    setDecisions((prev) => ({ ...prev, [current.id]: decision }));
    onResolve(current.id, decision, kind, cadence);
    setIdx((i) => i + 1);
  };

  const summary = {
    added: Object.values(decisions).filter((d) => d === 'confirmed').length,
    dismissed: Object.values(decisions).filter((d) => d === 'dismissed').length,
  };

  return (
    <ResponsiveDrawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className="flex size-full min-h-0 flex-col overflow-hidden px-0 data-[vaul-drawer-direction=bottom]:h-[92dvh] data-[vaul-drawer-direction=bottom]:max-h-[92dvh] data-[vaul-drawer-direction=right]:size-full data-[vaul-drawer-direction=right]:max-h-none sm:max-w-md"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="border-b border-border-subtle px-5 py-4 text-left">
          <DrawerTitle className="text-lg font-bold">Revisar detecções</DrawerTitle>
          <DrawerDescription>Confirme o que é recorrente</DrawerDescription>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {done ? (
            <ReviewDone summary={summary} onClose={() => onOpenChange(false)} />
          ) : current ? (
            <>
              <div className="mb-5 flex items-center gap-1.5" role="img" aria-label={`${idx + 1} de ${total}`}>
                {snapshot.map((c, i) => (
                  <span
                    key={c.id}
                    className={cn(
                      'h-1 flex-1 rounded-full',
                      i < idx ? 'bg-success' : i === idx ? 'bg-neutral-300' : 'bg-neutral-200',
                    )}
                  />
                ))}
              </div>
              <p className="mb-3 text-xs text-text-tertiary">{idx + 1} de {total}</p>
              <CandidateCard
                key={current.id}
                item={current}
                branding={connectorFor(current.accountId)}
                onConfirm={(kind, cadence) => decide('confirmed', kind, cadence)}
                onDismiss={() => decide('dismissed')}
                onSkip={() => setIdx((i) => i + 1)}
              />
            </>
          ) : null}
        </div>
      </DrawerContent>
    </ResponsiveDrawer>
  );
}

function CandidateCard({
  item,
  branding,
  onConfirm,
  onDismiss,
  onSkip,
}: {
  item: RecurringPayment;
  branding: ConnectorBranding | null;
  onConfirm: (kind?: RecurringPaymentKind, cadence?: RecurringPaymentCadence) => void;
  onDismiss: () => void;
  onSkip: () => void;
}) {
  const { getToken } = useAuth();
  const formatCurrency = useFormatCurrency();
  const taxonomyColors = useRecurringTaxonomyColors();
  const name = displayName(item);
  const confidence = Math.round((item.confidence ?? 0) * 100);
  const editable = EDITABLE_KINDS.includes(item.kind);
  const [tipo, setTipo] = useState<'assinatura' | 'conta_fixa'>(defaultReviewTipo(item.kind));
  const [frequencia, setFrequencia] = useState<'Monthly' | 'Yearly'>(
    item.cadence === 'Yearly' ? 'Yearly' : 'Monthly',
  );

  const [evidence, setEvidence] = useState<Transaction[]>([]);
  const [loadingEvidence, setLoadingEvidence] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingEvidence(true);
    getToken()
      .then((token) => (token ? recurringPaymentsApi.getTransactions(token, item.id, { limit: 4 }) : null))
      .then((page) => {
        if (!cancelled) setEvidence(page?.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setEvidence([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingEvidence(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, item.id]);

  const confirm = () => onConfirm(
    editable ? (tipo === 'conta_fixa' ? 'Utility' : 'Subscription') : undefined,
    editable && frequencia !== item.cadence ? frequencia : undefined,
  );

  return (
    <div className="rounded-2xl border border-border-subtle bg-surface p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {branding ? (
            <span
              className="flex size-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: branding.color ?? 'var(--neutral-600)' }}
            >
              {branding.initial}
            </span>
          ) : (
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-sm font-semibold text-text-secondary">
              {name.charAt(0).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <h3 className="truncate text-[17px] font-semibold text-text-primary">{name}</h3>
            <p className="text-xs text-text-tertiary">{cadenceLabel(item)}</p>
          </div>
        </div>
        {confidence > 0 && (
          <span className="shrink-0 rounded-full bg-surface-sunken px-2 py-1 font-mono text-2xs text-text-secondary tabular-nums">
            {confidence}%
          </span>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[28px] leading-none text-text-primary tabular-nums">{formatCurrency(item.amount)}</span>
          <span className="text-sm text-text-tertiary">{cadenceSuffix(item.cadence)}</span>
        </div>
        {item.cadence === 'Yearly' && (
          <p className="mt-1.5 font-mono text-xs text-text-tertiary tabular-nums">
            ≈ {formatCurrency(monthlyEquivalent(item.amount, item.cadence))}/mês
          </p>
        )}
      </div>

      {editable && (
        <>
          <p className="mb-2 text-xs text-text-tertiary">Classificar como</p>
          <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl border border-border-subtle bg-surface-sunken p-1">
            {([
              { key: 'assinatura', label: 'Assinatura', color: taxonomyColors.assinaturas },
              { key: 'conta_fixa', label: 'Conta fixa', color: taxonomyColors.fixas },
            ] as const).map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setTipo(option.key)}
                className={cn(
                  'flex min-h-9 items-center justify-center gap-1.5 rounded-lg text-sm font-medium transition-colors',
                  tipo === option.key ? 'bg-surface-raised text-text-primary shadow-sm' : 'text-text-secondary',
                )}
              >
                <span className="size-2 rounded-full" style={{ backgroundColor: option.color }} />
                {option.label}
              </button>
            ))}
          </div>

          <p className="mb-2 text-xs text-text-tertiary">Frequência</p>
          <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl border border-border-subtle bg-surface-sunken p-1">
            {([
              { key: 'Monthly', label: 'Mensal' },
              { key: 'Yearly', label: 'Anual' },
            ] as const).map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setFrequencia(option.key)}
                className={cn(
                  'flex min-h-9 items-center justify-center rounded-lg text-sm font-medium transition-colors',
                  frequencia === option.key ? 'bg-surface-raised text-text-primary shadow-sm' : 'text-text-secondary',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}

      <EvidenceList evidence={evidence} loading={loadingEvidence} formatCurrency={formatCurrency} />

      <button
        type="button"
        onClick={confirm}
        className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover"
      >
        <CheckIcon size={17} weight="bold" /> Confirmar recorrência
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="mt-2 flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-border-subtle bg-surface text-sm font-medium text-text-secondary transition-colors hover:bg-surface-sunken"
      >
        <XIcon size={15} weight="bold" /> Não é recorrente
      </button>
      <button
        type="button"
        onClick={onSkip}
        className="mt-1 min-h-10 w-full text-sm text-text-tertiary transition-colors hover:text-text-secondary"
      >
        Decidir depois
      </button>
    </div>
  );
}

function EvidenceList({
  evidence,
  loading,
  formatCurrency,
}: {
  evidence: Transaction[];
  loading: boolean;
  formatCurrency: ReturnType<typeof useFormatCurrency>;
}) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-sunken p-3">
      <p className="mb-2 text-2xs text-text-tertiary">Por que detectamos</p>
      {loading ? (
        <div className="flex items-center justify-center py-3 text-text-tertiary">
          <CircleNotchIcon size={16} className="animate-spin" />
        </div>
      ) : evidence.length === 0 ? (
        <p className="py-1 text-xs text-text-tertiary">Sem lançamentos vinculados ainda.</p>
      ) : (
        <ul className="space-y-1.5" role="list">
          {evidence.map((tx) => (
            <li key={tx.id} className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2 text-xs text-text-secondary">
                <span className="size-1.5 shrink-0 rounded-full bg-neutral-300" aria-hidden />
                {new Date(tx.referenceDate ?? tx.effectiveDate ?? tx.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
              </span>
              <span className="shrink-0 font-mono text-xs text-text-secondary tabular-nums">{formatCurrency(tx.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReviewDone({ summary, onClose }: { summary: { added: number; dismissed: number }; onClose: () => void }) {
  const nothing = summary.added === 0 && summary.dismissed === 0;
  return (
    <div className="flex flex-col items-center justify-center pt-16 text-center">
      <span className="mb-5 flex size-16 items-center justify-center rounded-full bg-success-surface text-success">
        <CheckIcon size={30} weight="bold" />
      </span>
      <h3 className="mb-1.5 text-lg font-semibold text-text-primary">Tudo revisado</h3>
      <p className="mb-8 text-sm text-text-secondary">
        {nothing
          ? 'Nada alterado'
          : [
              summary.added > 0 ? `${summary.added} ${summary.added === 1 ? 'adicionada' : 'adicionadas'}` : null,
              summary.dismissed > 0 ? `${summary.dismissed} ${summary.dismissed === 1 ? 'dispensada' : 'dispensadas'}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
      </p>
      <button
        type="button"
        onClick={onClose}
        className="min-h-12 rounded-xl bg-primary px-8 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover"
      >
        Concluir
      </button>
    </div>
  );
}
